import { Machine, assign } from 'xstate';
import _orderBy from 'lodash/orderBy';
import _uniqBy from 'lodash/uniqBy';

import {
    getEmptyEntitySelectionContext,
    sortByLatestDate,
    logDetails,
} from '@trqk/utils';

/**
 * State machine that handles entity selection for the current user.
 */
export const entitySelectionMachine = Machine<
    EntitySelectionContext,
    // EntitySelectionStateSchema,
    EntitySelectionEvent
>(
    {
        type: 'compound',
        initial: 'running',
        context: getEmptyEntitySelectionContext(),
        states: {
            running: {
                on: {
                    ADD_STATEMENT_TO_ENTITIES: {
                        // add 'logDetails' to an actions array to see the context and event
                        actions: [
                            'addStatementToEntities',
                            'runOnSelectEntity',
                        ],
                    },
                    CLEAR_CONTEXT: {
                        actions: ['clearContext', 'runOnSelectEntity'],
                    },
                    DELETE_ENTITY: {
                        actions: ['deleteEntity', 'runOnSelectEntity'],
                    },
                    DELETE_STATEMENT_FROM_ENTITIES: {
                        actions: [
                            'deleteStatementFromEntities',
                            'runOnSelectEntity',
                        ],
                    },
                    SET_DISABLE_SELECTION: {
                        actions: ['setDisableSelection'],
                    },
                    SET_DISABLE_VIEW_ALL: {
                        actions: ['setDisableViewAll'],
                    },
                    SET_ENTITY_DATA: {
                        actions: ['setEntityData', 'runOnSelectEntity'],
                    },
                    SET_SELECTED_ENTITY: {
                        actions: ['setSelectedEntity', 'runOnSelectEntity'],
                    },
                    SET_VIEW_ALL: {
                        actions: ['setViewAll'],
                    },
                },
            },
        },
    },
    {
        actions: {
            logDetails,
            /**
             * Add a new statement to the entity selection machine. If this is the first statement
             * for an entity, a new entity will be created.
             */
            addStatementToEntities: assign(
                (
                    context: EntitySelectionContext,
                    event: EntitySelectionEvent,
                ) => {
                    if (event.type !== 'ADD_STATEMENT_TO_ENTITIES') {
                        return context;
                    }

                    const {
                        value: { entityId, entity, statementView },
                    } = event;

                    const existingEntity = context.activeEntities.find(
                        ({ id }: ActiveEntity): boolean => id === entityId,
                    );

                    // If the entity already exists,
                    if (existingEntity !== undefined) {
                        const nextActiveEntities = context.activeEntities.map(
                            (activeEntity: ActiveEntity): ActiveEntity => {
                                if (activeEntity.id !== entityId) {
                                    return activeEntity;
                                }

                                const nextCompletedStatementViews = [
                                    ...activeEntity.completedStatementViews,
                                    statementView,
                                ].sort(
                                    (
                                        viewA: TrqkStatementView,
                                        viewB: TrqkStatementView,
                                    ): number =>
                                        sortByLatestDate(
                                            viewA?.start_date ?? null,
                                            viewB?.start_date ?? null,
                                        ),
                                );

                                // Add the statementView to the activeEntity's
                                // `completedStatementViews`
                                return {
                                    ...activeEntity,
                                    completedStatementViews:
                                        _uniqBy<TrqkStatementView>(
                                            nextCompletedStatementViews,
                                            'statement_id',
                                        ),
                                };
                            },
                        );

                        return {
                            ...context,
                            activeEntities: nextActiveEntities,
                        };
                    }

                    // Otherwise, if this is the first statement for this entity,

                    // Add the entity to context.entities
                    const nextEntities = [...context.entities, entity];

                    // Add an activeEntity to context.activeEntities
                    const nextActiveEntities = [
                        ...context.activeEntities,
                        {
                            id: entityId,
                            entity,
                            completedStatementViews: [statementView],
                        },
                    ];

                    let nextSelectedEntity = context.selectedEntity;
                    let nextSelectedEntityId = context.selectedEntityId;

                    if (context.selectedEntity === null) {
                        // If there is not currently a selected entity, make the new entity
                        // the selected entity
                        nextSelectedEntity = entity;
                        nextSelectedEntityId = entityId;
                    }

                    return {
                        ...context,
                        entities: nextEntities,
                        activeEntities: nextActiveEntities,
                        selectedEntity: nextSelectedEntity,
                        selectedEntityId: nextSelectedEntityId,
                    };
                },
            ),
            /**
             * Clear EntitySelectionContext on sign out
             */
            clearContext: assign(
                (
                    context: EntitySelectionContext,
                    event: EntitySelectionEvent,
                ) => {
                    if (event.type !== 'CLEAR_CONTEXT') {
                        return context;
                    }

                    return getEmptyEntitySelectionContext();
                },
            ),
            /**
             * Delete an entity by its id
             */
            deleteEntity: assign(
                (
                    context: EntitySelectionContext,
                    event: EntitySelectionEvent,
                ) => {
                    if (event.type !== 'DELETE_ENTITY') {
                        return context;
                    }

                    const {
                        value: { id },
                    } = event;

                    const {
                        activeEntities: prevActiveEntities,
                        entities: prevEntities,
                        priorSelectedEntityId: prevPriorSelectedEntityId,
                        selectedEntity: prevSelectedEntity,
                        selectedEntityId: prevSelectedEntityId,
                    } = context;

                    // Filter entity from activeEntities and entities
                    const activeEntities = prevActiveEntities.filter(
                        (activeEntity: ActiveEntity): boolean =>
                            activeEntity.id !== id,
                    );
                    const entities = prevEntities.filter(
                        (entity: TrqkEntity): boolean => entity.id !== id,
                    );

                    let priorSelectedEntityId = prevPriorSelectedEntityId;
                    let selectedEntityId = prevSelectedEntityId;
                    let selectedEntity = prevSelectedEntity;

                    // If previous priorSelectedEntityId has been deleted, set it to null
                    if (id === prevPriorSelectedEntityId) {
                        priorSelectedEntityId = null;
                    }

                    // If previous selectedEntity / selectedEntityId have been deleted,
                    if (id === prevSelectedEntityId) {
                        // If there is at least one remaining entity, select the first one
                        if (entities.length > 0) {
                            [selectedEntity] = entities;
                            selectedEntityId = entities[0].id;
                        } else {
                            // Otherwise, select null
                            selectedEntityId = null;
                            selectedEntity = null;
                        }
                    }

                    return {
                        ...context,
                        activeEntities,
                        entities,
                        priorSelectedEntityId,
                        selectedEntityId,
                        selectedEntity,
                    };
                },
            ),
            deleteStatementFromEntities: assign(
                (
                    context: EntitySelectionContext,
                    event: EntitySelectionEvent,
                ) => {
                    if (event.type !== 'DELETE_STATEMENT_FROM_ENTITIES') {
                        return context;
                    }

                    const {
                        value: { statementId },
                    } = event;

                    const existingEntityIndex =
                        context.activeEntities.findIndex(
                            ({
                                completedStatementViews,
                            }: ActiveEntity): boolean =>
                                completedStatementViews.some(
                                    (view: TrqkStatementView): boolean =>
                                        view.statement_id === statementId,
                                ),
                        );

                    if (existingEntityIndex > -1) {
                        const existingEntity =
                            context.activeEntities[existingEntityIndex];
                        const nextCompletedStatementViews =
                            existingEntity.completedStatementViews.filter(
                                (view: TrqkStatementView): boolean =>
                                    view.statement_id !== statementId,
                            );
                        const nextActiveEntities = context.activeEntities.map(
                            (
                                activeEntity: ActiveEntity,
                                index: number,
                            ): ActiveEntity => {
                                if (index === existingEntityIndex) {
                                    return {
                                        ...activeEntity,
                                        completedStatementViews:
                                            nextCompletedStatementViews,
                                    };
                                }

                                return activeEntity;
                            },
                        );

                        // If the entity for this statement has other statements in its
                        // completedStatementViews, just remove this statementId from
                        // completedStatementViews.
                        if (nextCompletedStatementViews.length > 0) {
                            return {
                                ...context,
                                activeEntities: nextActiveEntities,
                            };
                        }

                        // If the entity for this statement has no other statements, remove all
                        // elements of this entity from context. Use the first entity in
                        // activeEntities as the next selectedEntity.

                        nextActiveEntities.splice(existingEntityIndex, 1);

                        const deletedEntityIsSelected =
                            context?.selectedEntity?.id === existingEntity.id;

                        let nextSelectedEntity = context.selectedEntity;

                        if (deletedEntityIsSelected) {
                            const sortedActiveEntities = _orderBy<ActiveEntity>(
                                nextActiveEntities,
                                ['entity.name'],
                                ['asc'],
                            );

                            nextSelectedEntity =
                                sortedActiveEntities?.[0]?.entity ?? null;
                        }

                        const nextSelectedEntityId =
                            nextSelectedEntity?.id ?? null;

                        return {
                            ...context,
                            activeEntities: nextActiveEntities,
                            entities: context.entities.filter(
                                (entity: TrqkEntity): boolean =>
                                    entity.id !== existingEntity.id,
                            ),
                            priorSelectedEntityId:
                                context.priorSelectedEntityId ===
                                existingEntity.id
                                    ? null
                                    : context.priorSelectedEntityId,
                            selectedEntity: nextSelectedEntity,
                            selectedEntityId: nextSelectedEntityId,
                        };
                    }

                    return context;
                },
            ),
            setDisableSelection: assign(
                (
                    context: EntitySelectionContext,
                    event: EntitySelectionEvent,
                ) => {
                    if (event.type !== 'SET_DISABLE_SELECTION') {
                        return context;
                    }

                    const {
                        value: { disableSelection },
                    } = event;

                    return {
                        ...context,
                        disableSelection,
                    };
                },
            ),
            setDisableViewAll: assign(
                (
                    context: EntitySelectionContext,
                    event: EntitySelectionEvent,
                ) => {
                    if (event.type !== 'SET_DISABLE_VIEW_ALL') {
                        return context;
                    }

                    const {
                        value: { disableViewAll },
                    } = event;

                    return {
                        ...context,
                        disableViewAll,
                    };
                },
            ),
            setEntityData: assign(
                (
                    context: EntitySelectionContext,
                    event: EntitySelectionEvent,
                ) => {
                    if (event.type !== 'SET_ENTITY_DATA') {
                        return context;
                    }
                    const {
                        value: { activeEntities, entities, onSelectEntity },
                    } = event;
                    const { priorSelectedEntityId: prevPriorSelectedEntityId } =
                        context;
                    let priorSelectedEntityId = prevPriorSelectedEntityId;

                    // selectedEntityId is read from the `entity_id` URL param
                    let {
                        value: { selectedEntityId },
                    } = event;

                    let selectedEntity =
                        selectedEntityId === null
                            ? null
                            : entities.find(
                                  (entity: TrqkEntity): boolean =>
                                      entity.id === selectedEntityId,
                              ) ?? null;

                    if (
                        // If the entity named by selectedEntityId is not available
                        selectedEntity === null
                    ) {
                        if (entities.length > 0) {
                            // and there is at least one available entity, select the first incoming
                            // entity
                            [selectedEntity] = entities;
                            selectedEntityId = selectedEntity.id;
                        } else {
                            // Otherwise, set selectedEntityId to null
                            selectedEntityId = null;
                        }
                    }

                    if (
                        !entities.some(
                            (entity: TrqkEntity): boolean =>
                                entity.id === prevPriorSelectedEntityId,
                        )
                    ) {
                        // If the previous priorSelectedEntityId is no longer available, set it to
                        // null
                        priorSelectedEntityId = null;
                    }

                    return {
                        ...context,
                        activeEntities,
                        entities,
                        onSelectEntity,
                        selectedEntity,
                        selectedEntityId,
                        priorSelectedEntityId,
                    };
                },
            ),
            setSelectedEntity: assign(
                (
                    context: EntitySelectionContext,
                    event: EntitySelectionEvent,
                ) => {
                    if (event.type !== 'SET_SELECTED_ENTITY') {
                        return context;
                    }

                    let {
                        value: { selectedEntityId },
                    } = event;

                    const { entities } = context;

                    const priorSelectedEntityId =
                        context?.selectedEntity?.id ?? null;

                    const selectedEntity =
                        entities.find(
                            ({ id }: TrqkEntity): boolean =>
                                id === selectedEntityId,
                        ) ?? null;

                    // Do not set a selectedEntityId that does not actually correspond to an
                    // existing entity
                    if (selectedEntity === null) {
                        selectedEntityId = null;
                    }

                    return {
                        ...context,
                        selectedEntityId,
                        priorSelectedEntityId,
                        selectedEntity,
                    };
                },
            ),
            setViewAll: assign(
                (
                    context: EntitySelectionContext,
                    event: EntitySelectionEvent,
                ) => {
                    if (event.type !== 'SET_VIEW_ALL') {
                        return context;
                    }

                    const {
                        value: { viewAll },
                    } = event;

                    return {
                        ...context,
                        viewAll,
                    };
                },
            ),
            /**
             * Check for presence of the onSelectEntity callback and invoke it if it exists
             */
            runOnSelectEntity: (context: EntitySelectionContext): void => {
                const { onSelectEntity, selectedEntityId } = context;

                if (onSelectEntity) {
                    onSelectEntity(selectedEntityId);
                }
            },
        },
    },
);
